import { conditionFactsSummary, conditionLabel, readConditionCheck, readConditionFacts } from './absenceRequestConditions';
import { absenceDayPartLabel } from './absenceDayPart';
import { AbsenceLinkedEvidenceList, type AbsenceEvidenceReadPort } from './AbsenceRequestEvidenceSection';
import { Box, Stack, Typography } from '@mui/material';
import { DefinitionGrid, SectionCard, ValueBadge, designTokens } from '../../design-system';

type TimeOffWorkflowRequestSummaryProps = {
  values: Record<string, unknown>;
  columns?: 2 | 3 | 4;
  requestId?: string;
  evidencePort?: AbsenceEvidenceReadPort;
  fixtureMode?: boolean;
};

function textValue(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function dateLabel(value: unknown): string {
  const normalized = textValue(value);
  return normalized ? normalized.replaceAll('-', '.') : '';
}

function periodLabel(startDate: unknown, endDate: unknown): string {
  const start = dateLabel(startDate);
  const end = dateLabel(endDate);
  if (!start && !end) return '';
  if (!end || start === end) return start;
  return `${start} – ${end}`;
}

function quantityLabel(quantity: unknown, unitOfTime: unknown): string {
  const normalizedQuantity = textValue(quantity);
  if (!normalizedQuantity) return '';
  const unit = textValue(unitOfTime).toUpperCase();
  const unitLabel = unit.includes('HOUR') ? '시간' : unit.includes('MINUTE') ? '분' : '일';
  const numeric = Number(normalizedQuantity);
  const displayQuantity = Number.isFinite(numeric)
    ? numeric.toLocaleString('ko-KR', { maximumFractionDigits: 2 })
    : normalizedQuantity;
  return `${displayQuantity}${unitLabel}`;
}

function evidenceLabel(value: unknown): string {
  if (Array.isArray(value)) {
    const count = value.map(textValue).filter(Boolean).length;
    return count ? `${count}개 문서` : '제출한 증빙 없음';
  }
  return textValue(value);
}

export function TimeOffWorkflowRequestSummary({ values, columns = 2, requestId, evidencePort, fixtureMode }: TimeOffWorkflowRequestSummaryProps) {
  const facts = conditionFactsSummary(readConditionFacts(values.conditionFacts));
  const condition = readConditionCheck(values.conditionCheck);
  const limits = values.managementMode === 'USAGE_LIMIT' && Array.isArray(values.usageLimits) ? values.usageLimits.flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const limit = value as Record<string, unknown>;
    if (![limit.periodStart, limit.periodEnd, limit.limitQuantity, limit.requestedQuantity].every((field) => textValue(field))) return [];
    return [{ period: periodLabel(limit.periodStart, limit.periodEnd), limit: quantityLabel(limit.limitQuantity, values.unitOfTime), requested: quantityLabel(limit.requestedQuantity, values.unitOfTime) }];
  }) : [];
  return <Box
    component="section"
    aria-labelledby="workflow-time-off-request-summary-title"
    data-testid="workflow-time-off-request-summary"
    sx={{ minWidth: 0 }}
  >
    <SectionCard
      title={<Typography id="workflow-time-off-request-summary-title" component="h2" variant="h6">휴가 정보</Typography>}
      showDivider={false}
      bodyPadding="standard"
    >
      <DefinitionGrid
        columns={columns}
        emphasizeValues
        emptyValue="미입력"
        testId="workflow-time-off-request-values"
        items={[
          { id: 'type', label: '휴가 유형', value: textValue(values.timeOffTypeDescriptor) ? <ValueBadge label={textValue(values.timeOffTypeDescriptor)} /> : '' },
          { id: 'period', label: '사용 기간', value: periodLabel(values.startDate, values.endDate) },
          ...(absenceDayPartLabel(values.dayPart) ? [{ id: 'dayPart', label: '사용 시간대', value: absenceDayPartLabel(values.dayPart) }] : []),
          { id: 'quantity', label: '사용량', value: quantityLabel(values.quantity, values.unitOfTime) },
          ...facts.map(([label, value], index) => ({ id: `condition-fact-${index}`, label, value, wide: true })),
          { id: 'reason', label: '휴가 메모', value: textValue(values.reason), wide: true },
          { id: 'evidence', label: '증빙 문서', value: requestId && evidencePort ? <AbsenceLinkedEvidenceList requestId={requestId} port={evidencePort} fixtureMode={fixtureMode}
            requirement={{ policySnapshot: textValue(values.evidencePolicySnapshot) }} /> : evidenceLabel(values.evidenceDocuments), wide: true },
        ]}
      />
      {condition && condition.requestCondition !== 'NONE' && <Stack component="section" aria-label="신청 시 조건 확인" spacing={1} sx={{ mt: `${designTokens.space.md}px` }}>
        <Typography component="h3" variant="subtitle2">신청 시 조건 확인</Typography>
        <Typography variant="body2">{conditionLabel(condition)}</Typography>
        {condition.requestCondition === 'UNPAID' && condition.prerequisiteBalance !== undefined && <Typography variant="body2">{condition.prerequisiteTimeOffType?.descriptor || '연차'} 신청 가능 잔액 {quantityLabel(condition.prerequisiteBalance, 'DAYS')} · 승인 대기 반영 · 신청 당시 기준</Typography>}
        {condition.messages.map((message) => <Typography key={message} variant="body2" color="text.secondary">{message}</Typography>)}
        <Typography variant="caption" color="text.secondary">{condition.requestCondition === 'HOSPITALIZATION' ? '증빙의 입원 기간과 회사 제출 절차를 확인해 주세요.' : '신청 사유의 적정성은 처리자가 확인해 주세요.'}</Typography>
      </Stack>}
      {limits.length > 0 && <Stack component="section" aria-label="신청 시 적용 한도" spacing={1} sx={{ mt: `${designTokens.space.md}px` }}>
        <Typography component="h3" variant="subtitle2">신청 시 적용 한도</Typography>
        {limits.map((limit) => <Typography key={limit.period} variant="body2">{limit.period} · 한도 {limit.limit} · 이번 신청 {limit.requested}</Typography>)}
      </Stack>}
    </SectionCard>
  </Box>;
}
